import React, { useMemo } from 'react';
import type { ForwardValuationWidgetInstanceConfig, ForwardValuationData } from './types';
import { formatMultiple, DEFAULT_COLORS } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { ChartWidget, KeyMetricBadge } from '../../../blocks/chart-widget';
import type { ChartSpec } from '../../../blocks/chart-widget';
import type { LineChartSeries, ChartLegendSeries } from '../../../blocks/charts';

const SAMPLE_DATA: ForwardValuationData = {
  ticker: 'SAMPLE',
  ntm_pe: 35.3,
  ntm_ev_ebitda: 26.6,
  forward_multiples: [
    { fiscal_year: 2026, forward_pe: 35.3, forward_ev_ebitda: 26.6 },
    { fiscal_year: 2027, forward_pe: 32.1, forward_ev_ebitda: 24.5 },
    { fiscal_year: 2028, forward_pe: 29.2, forward_ev_ebitda: 23.0 },
  ],
};

interface Props {
  config: ForwardValuationWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const ForwardValuationWidgetRenderer: React.FC<Props> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<ForwardValuationData>(
    'forward_valuation',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const d = uid ? data : SAMPLE_DATA;

  const chartData = useMemo(() => {
    const rows = d?.forward_multiples ?? [];
    return rows
      .filter((r) => typeof r.forward_pe === 'number' || typeof r.forward_ev_ebitda === 'number')
      // Forward years ascending so the line reads left→right oldest→newest,
      // regardless of the order the provider returns them in.
      .sort((a, b) => Number(a.fiscal_year ?? 0) - Number(b.fiscal_year ?? 0))
      .map((r) => ({
        year: r.fiscal_year != null ? String(r.fiscal_year) : '',
        forward_pe: r.forward_pe,
        forward_ev_ebitda: r.forward_ev_ebitda,
      }));
  }, [d]);

  // Forward P/E and EV/EBITDA as lines (both are multiples, shared axis) — the
  // multiple compresses/expands across forward years, which reads as a trend, so
  // a line conveys it better than bars.
  const series = useMemo<LineChartSeries[]>(() => [
    { yKey: 'forward_pe', yName: 'Fwd P/E', axis: 'a', color: DEFAULT_COLORS[0], markers: true, interpolation: 'linear' },
    { yKey: 'forward_ev_ebitda', yName: 'Fwd EV/EBITDA', axis: 'a', color: DEFAULT_COLORS[1], markers: true, interpolation: 'linear' },
  ], []);

  // Names-only legend — the header badges carry the headline NTM values.
  const legendSeries = useMemo<ChartLegendSeries[]>(() => series.map((s) => ({
    key: s.yKey,
    name: s.yName,
    color: s.color as string,
  })), [series]);

  const spec: ChartSpec = {
    chartKind: 'line',
    rows: chartData,
    xKey: 'year',
    lineSeries: series,
    showEndValueTags: false,
    yLabelFormatter: (params) => formatMultiple(Number(params.value)),
    xTitle: 'Fiscal year',
    yTitles: { a: 'Multiple' },
    legendSeries,
    footnote: 'Source: FMP',
    emptyMessage: 'No forward estimates available',
  };

  const header = (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
      <div className="flex items-center gap-2 mr-1">
        <span className="text-sm font-semibold text-neutral-800">Forward Valuation</span>
        <span className="text-[11px] uppercase tracking-wide text-neutral-400">NTM</span>
      </div>
      <KeyMetricBadge label="NTM P/E" value={formatMultiple(d?.ntm_pe ?? null)} />
      <KeyMetricBadge label="NTM EV/EBITDA" value={formatMultiple(d?.ntm_ev_ebitda ?? null)} />
    </div>
  );

  return (
    <ChartWidget loading={loading} error={error} refetch={refetch} isPreview={isPreview} spec={spec} header={header} />
  );
};

export default ForwardValuationWidgetRenderer;
